import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface Restaurant {
  id: string;
  name: string;
  location: string | null;
  is_headquarters: boolean;
  organization_id: string | null;
  service_type: string | null;
  status?: string;
}

interface RestaurantContextType {
  currentRestaurantId: string | null;
  currentRestaurant: Restaurant | null;
  availableRestaurants: Restaurant[];
  switchRestaurant: (restaurantId: string) => Promise<void>;
  isLoading: boolean;
  isSwitching: boolean;  // NEW: Global switching state for loader
  isMultiStore: boolean;
  refreshRestaurants: () => Promise<void>;
  isOwnerOfAny: boolean;  // User owns at least one restaurant
  userRole: 'owner' | 'member';  // User's role in the system
}

const RestaurantContext = createContext<RestaurantContextType | undefined>(undefined);

const STORAGE_KEY = 'alacart-current-restaurant-id';

export function RestaurantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [currentRestaurantId, setCurrentRestaurantId] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY);
  });
  const [currentRestaurant, setCurrentRestaurant] = useState<Restaurant | null>(null);
  const [availableRestaurants, setAvailableRestaurants] = useState<Restaurant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);  // NEW: Switching state
  const [isOwnerOfAny, setIsOwnerOfAny] = useState(false);

  // Buscar restaurantes acessíveis ao usuário
  const fetchRestaurants = useCallback(async () => {
    if (!user?.id) {
      setAvailableRestaurants([]);
      setCurrentRestaurant(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Buscar restaurantes onde o usuário é dono (todos os status para Master Admin ver unidades pendentes)
      const { data: ownedRestaurants, error: ownedError } = await (supabase as any)
        .from('restaurants')
        .select('id, name, location, is_headquarters, organization_id, service_type, status')
        .eq('user_id', user.id)
        .in('status', ['active', 'pending_approval', 'waiting_payment']);

      if (ownedError) throw ownedError;

      // Buscar restaurantes onde o usuário é membro (mas não dono)
      const { data: memberRestaurants, error: memberError } = await (supabase as any)
        .from('restaurant_members')
        .select(`
          restaurant:restaurants(id, name, location, is_headquarters, organization_id, service_type)
        `)
        .eq('user_id', user.id);

      if (memberError && memberError.code !== 'PGRST116') {
        console.warn('Error fetching member restaurants:', memberError);
      }

      // Combinar e remover duplicatas
      const memberRests = (memberRestaurants || [])
        .map((m: any) => m.restaurant)
        .filter(Boolean);
      
      const allRestaurants = [...(ownedRestaurants || [])];
      memberRests.forEach((mr: Restaurant) => {
        if (!allRestaurants.find(r => r.id === mr.id)) {
          allRestaurants.push(mr);
        }
      });

      // Ordenar: Sede primeiro
      allRestaurants.sort((a, b) => {
        if (a.is_headquarters && !b.is_headquarters) return -1;
        if (!a.is_headquarters && b.is_headquarters) return 1;
        return a.name.localeCompare(b.name);
      });

      setAvailableRestaurants(allRestaurants);
      
      // Determine if user is owner of any restaurant
      const ownerOfAny = (ownedRestaurants?.length || 0) > 0;
      setIsOwnerOfAny(ownerOfAny);
      let selectedId = currentRestaurantId;
      
      // Se não tem ID salvo ou o ID salvo não está na lista, usar o primeiro
      if (!selectedId || !allRestaurants.find(r => r.id === selectedId)) {
        selectedId = allRestaurants[0]?.id || null;
        if (selectedId) {
          localStorage.setItem(STORAGE_KEY, selectedId);
          setCurrentRestaurantId(selectedId);
        }
      }

      // Definir restaurante atual completo
      const current = allRestaurants.find(r => r.id === selectedId) || null;
      setCurrentRestaurant(current);

    } catch (error) {
      console.error('Error fetching restaurants:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, currentRestaurantId]);

  // Trocar de restaurante com validação de acesso
  const switchRestaurant = useCallback(async (restaurantId: string) => {
    // Validação de segurança: verificar se o usuário tem acesso a esta loja
    const hasAccess = availableRestaurants.some(r => r.id === restaurantId);
    if (!hasAccess) {
      toast.error('Você não tem acesso a esta unidade');
      return;
    }

    const restaurant = availableRestaurants.find(r => r.id === restaurantId);
    if (!restaurant) {
      toast.error('Restaurante não encontrado');
      return;
    }

    // Set switching state to show global loader
    setIsSwitching(true);

    try {
      // Salvar no localStorage
      localStorage.setItem(STORAGE_KEY, restaurantId);
      setCurrentRestaurantId(restaurantId);
      setCurrentRestaurant(restaurant);

      // RESET (not just invalidate) all queries to prevent data leakage between units
      await queryClient.resetQueries();

      toast.success(`Trocado para ${restaurant.name}`);
    } finally {
      setIsSwitching(false);
    }
  }, [availableRestaurants, queryClient]);

  // Refrescar lista de restaurantes
  const refreshRestaurants = useCallback(async () => {
    await fetchRestaurants();
  }, [fetchRestaurants]);

  // Buscar restaurantes quando usuário muda
  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  // Atualizar currentRestaurant quando availableRestaurants ou currentRestaurantId mudam
  useEffect(() => {
    if (currentRestaurantId && availableRestaurants.length > 0) {
      const current = availableRestaurants.find(r => r.id === currentRestaurantId);
      if (current) {
        setCurrentRestaurant(current);
      }
    }
  }, [currentRestaurantId, availableRestaurants]);

  const userRole = isOwnerOfAny ? 'owner' : 'member';

  const value: RestaurantContextType = {
    currentRestaurantId,
    currentRestaurant,
    availableRestaurants,
    switchRestaurant,
    isLoading,
    isSwitching,  // NEW: Export switching state
    isMultiStore: availableRestaurants.length > 1,
    refreshRestaurants,
    isOwnerOfAny,
    userRole,
  };

  return (
    <RestaurantContext.Provider value={value}>
      {children}
    </RestaurantContext.Provider>
  );
}

export function useRestaurant() {
  const context = useContext(RestaurantContext);
  if (context === undefined) {
    throw new Error('useRestaurant must be used within a RestaurantProvider');
  }
  return context;
}

// Hook para compatibilidade com código existente que espera restaurantId
export function useCurrentRestaurantId() {
  const { currentRestaurantId } = useRestaurant();
  return currentRestaurantId;
}
